import { PaymentIntent } from '@stripe/stripe-js'
import { atom, selector } from 'recoil'
import {
  PreviewProrationResponse,
  ProductWithPrices,
  UpdatedSubscriptionResponse,
  CancelledSubscriptionResponse,
  SubscriptionInterval,
} from 'src/types/payments'
import { keyFactory } from 'src/utils/recoil'
import { paymentsClient } from 'src/clients'
import Stripe from 'stripe'
import { browserLogger } from 'src/utils/browser-logger'

import * as userAtoms from 'src/atoms/user'
import * as billingAtoms from 'src/atoms/billing'

const key = keyFactory('payments')

//  Subscription plans
export const paymentPlans = selector({
  key: key('payment-plans'),
  get: async () => {
    const resp = await paymentsClient.getProducts()
    return resp.data
  },
})

// The default product for self-serve subscriptions
const defaultProduct = selector<ProductWithPrices | undefined>({
  key: key('default-product'),
  get: ({ get }) => {
    const products = get(paymentPlans)
    return products?.[3]
  },
})

// Selected plan is the product, not the price
export const selectedPaymentPlan = atom<ProductWithPrices | undefined>({
  key: key('selected-payment-plan'),
  default: defaultProduct,
})

const defaultPaymentPlanPrice = selector({
  key: key('default-payment-plan-price'),
  get: ({ get }) => {
    const interval = get(subscriptionInterval)
    const selectedPlan = get(selectedPaymentPlan)
    if (!selectedPlan) {
      return undefined
    }
    return interval === 'year' ? selectedPlan.prices[1] : selectedPlan.prices[0]
  },
})

// Selected price is the price, either monthly or yearly
export const selectedPaymentPlanPrice = atom<Stripe.Price | undefined>({
  key: key('selected-payment-plan-price'),
  default: defaultPaymentPlanPrice,
})

export const subscriptionType = selector<'new' | 'existing' | undefined>({
  key: key('subscription-type'),
  get: ({ get }) => {
    const currentUser = get(userAtoms.currentUser)
    const canCancel = get(canCancelSubscription)

    if (!currentUser) {
      return undefined
    }
    if (currentUser.tier === 'free' || !canCancel) {
      return 'new'
    }
    return 'existing'
  },
})

export const subcribeModalOpen = atom<boolean>({
  key: key('subscribe-modal-open'),
  default: false,
})

const defaultSubscriptionInterval = selector<SubscriptionInterval>({
  key: key('default-subscription-interval'),
  get: ({ get }) => {
    const subscription = get(billingAtoms.subscription)
    if (subscription?.interval === 'year') {
      return 'year'
    }
    return 'month'
  },
})

export const subscriptionInterval = atom<SubscriptionInterval>({
  key: key('subscription-interval'),
  default: defaultSubscriptionInterval,
})

// Used to create a new subscription for stripe checkout form
export const clientSecret = selector<string | undefined | { error: any }>({
  key: 'client-secret',
  get: async ({ get }) => {
    // Only create a subscription if the user is not already subscribed
    const _subscriptionType = get(subscriptionType)
    if (!_subscriptionType || _subscriptionType === 'existing') {
      return undefined
    }

    const selectedPlan = get(selectedPaymentPlan)
    if (!selectedPlan) {
      return undefined
    }
    const selectedPrice = get(selectedPaymentPlanPrice)
    if (!selectedPrice) {
      return undefined
    }

    try {
      const { clientSecret } = await paymentsClient.createSubscription({
        priceId: selectedPrice.id,
      })
      return clientSecret
    } catch (e) {
      browserLogger.error(e)
      return {
        error: e,
      }
    }
  },
})

export const paymentIntent = atom<PaymentIntent | undefined>({
  key: key('payment-intent'),
  default: undefined,
})

// Subscription Updates (Upgrade/Downgrade)
export const subscriptionUpdateType = atom<'upgrade' | 'downgrade' | undefined>({
  key: key('subscription-update-type'),
  default: 'upgrade',
})

export const SUBSCRIPTION_UPDATE_STEPS = {
  PLANS: 0,
  PAYMENT: 1, // If they want to change their payment method
  CONFIRMATION: 2,
}
type UpdateStep = (typeof SUBSCRIPTION_UPDATE_STEPS)[keyof typeof SUBSCRIPTION_UPDATE_STEPS]

export const subscriptionUpdateStep = atom<UpdateStep>({
  key: key('subscription-update-step'),
  default: 0,
})

// Preview proration for a subscription change (only for an upgrade)
export const previewProration = selector<PreviewProrationResponse | undefined>({
  key: key('preview-proration'),
  get: async ({ get }) => {
    const updateType = get(subscriptionUpdateType)
    const _subscriptionType = get(subscriptionType)

    if (!_subscriptionType || _subscriptionType === 'new' || updateType === 'downgrade') {
      return undefined
    }

    const selectedPlan = get(selectedPaymentPlan)
    if (!selectedPlan) {
      return undefined
    }
    const selectedPrice = get(selectedPaymentPlanPrice)
    if (!selectedPrice) {
      return undefined
    }

    return paymentsClient.previewProration({
      priceId: selectedPrice.id,
    })
  },
})

// Updated subscription response with new plan
export const updatedSubscription = atom<UpdatedSubscriptionResponse | undefined>({
  key: key('updated-subscription'),
  default: undefined,
})

// Subscription Cancellations
export const canCancelSubscription = selector<boolean>({
  key: key('can-cancel-subscription'),
  get: async ({ get }) => {
    const subscription = get(billingAtoms.subscription)

    return !!subscription && (subscription.status === 'active' || subscription.status === 'trialing')
  },
})

export const cancelSubscriptionModalOpen = atom<boolean>({
  key: key('cancel-subscription-modal-open'),
  default: false,
})

export const SUBSCRIPTION_CANCELLATION_STEPS = {
  SURVEY: 0,
  VERIFY: 1,
  CONFIRMATION: 2,
} as const
type CancelStep = (typeof SUBSCRIPTION_CANCELLATION_STEPS)[keyof typeof SUBSCRIPTION_CANCELLATION_STEPS]

export const cancellationStep = atom<CancelStep>({
  key: key('cancellation-step'),
  default: 0,
})

const SURVEY_OPTIONS: Array<{
  id: string
  label: string
  checked: boolean
}> = [
  {
    id: 'lack-of-features',
    label: 'Lack of features',
    checked: false,
  },
  {
    id: 'too-expensive',
    label: 'Too expensive',
    checked: false,
  },

  {
    id: 'switching-to-competitor',
    label: `We're switching to a competitor`,
    checked: false,
  },
  {
    id: 'not-enough-value',
    label: `Not enough value for our business`,
    checked: false,
  },
]

export const cancellationSurveyOptions = atom<typeof SURVEY_OPTIONS>({
  key: key('cancellation-survey-options'),
  default: SURVEY_OPTIONS,
})

export const cancelledSubscription = atom<CancelledSubscriptionResponse | undefined>({
  key: key('cancelled-subscription'),
  default: undefined,
})

export const trialedStatus = selector<boolean>({
  key: key('trialed-status'),
  get: ({ get }) => {
    const user = get(userAtoms.currentUser)
    if (!user || user.tier === 'premium') {
      return true
    }
    return paymentsClient.getTrialedStatus()
  },
})
